import { 
    BlockStack, 
    Button, 
    Card, 
    Checkbox, 
    InlineStack, 
    Page, 
    RadioButton, 
    Tag, 
    Text, 
    TextField, 
    Toast 
} from "@shopify/polaris";
import React, { useCallback, useState, useEffect } from "react";
import { useAppFormik } from "../contexts/FormikContext";
import { useDispatch, useSelector } from 'react-redux';
import { createSettingAsync, getAllSetting, getSettingAsync } from "../redux/slice/commonSlice";
import ContextSaveBar from "../components/ContextSaveBar";

const defaultRedirectionData = {
    enabled: false,
    mode: "monitor",
    allowedDomains: [],
    blockExternalForms: false,
    showWarningMessage: true,
    warningMessage: "This external link is restricted by the store.",
    logRedirects: false
};

const RedirectPreventionPage = () => {
    const { values, setFieldValue, initialValues, resetForm } = useAppFormik();
    const dispatch = useDispatch();
    const { isCreateSettingLoading } = useSelector(getAllSetting);
    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [domainInput, setDomainInput] = useState("");

    // Get redirection data from Formik values
    const redirection = values.redirection || defaultRedirectionData;
    const allowedDomains = redirection.allowedDomains || [];

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? (
        <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
    ) : null;

    // Helper function to mark changes
    const markAsChanged = () => {
        setHasUnsavedChanges(true);
    };

    // Sanitize domain input
    const sanitizeDomain = (domain) => {
        return domain
            .trim()
            .replace(/^https?:\/\//, '')
            .replace(/\/$/, '')
            .toLowerCase();
    };

    const handleAddDomain = () => {
        const sanitized = sanitizeDomain(domainInput);
        if (sanitized && !allowedDomains.includes(sanitized)) {
            const updatedDomains = [...allowedDomains, sanitized];
            setFieldValue("redirection.allowedDomains", updatedDomains);
            setDomainInput("");
            markAsChanged();
        }
    };

    const handleRemoveDomain = (domain) => {
        const updatedDomains = allowedDomains.filter((d) => d !== domain);
        setFieldValue("redirection.allowedDomains", updatedDomains);
        markAsChanged();
    };

    const handleSave = async () => {
        await dispatch(createSettingAsync({ payload: { settings: values }, callback: handleCallBack }));
    };

    const handleCallBack = () => {
        dispatch(getSettingAsync({}));
        showToast("Redirect prevention settings saved successfully!");
        setHasUnsavedChanges(false);
    };

    const handleDiscard = () => {
        resetForm({ values: initialValues });
        setHasUnsavedChanges(false);
        showToast("Changes discarded");
    };

    // Initialize redirection data if not exists
    useEffect(() => {
        if (!values.redirection) {
            setFieldValue("redirection", defaultRedirectionData);
        }
    }, [values.redirection, setFieldValue]);

    return (
        <Page
            title="Redirect Prevention"
            subtitle="Control and manage external redirection settings from your store."
        >
            <BlockStack gap="500">
                {/* Section 1: Enable Redirect Prevention */}
                <Card>
                    <BlockStack gap="400">
                        <Text variant="headingMd" as="h2">
                            Enable Redirect Prevention
                        </Text>
                        <Checkbox
                            label="Enable redirect prevention"
                            checked={redirection.enabled || false}
                            onChange={(val) => {
                                setFieldValue("redirection.enabled", val);
                                markAsChanged();
                            }}
                            helpText="When enabled, external redirects will be controlled based on your settings below."
                        />
                    </BlockStack>
                </Card>

                {/* Section 2: Redirect Control Mode */}
                <Card>
                    <BlockStack gap="400">
                        <Text variant="headingMd" as="h2">
                            Redirect Control Mode
                        </Text>
                        <BlockStack gap="300">
                            <RadioButton
                                label="Block all external URLs"
                                checked={redirection.mode === "block_all"}
                                id="block_all"
                                name="redirectMode"
                                onChange={() => {
                                    setFieldValue("redirection.mode", "block_all");
                                    markAsChanged();
                                }}
                                helpText="All external links will be blocked and users will see the warning message."
                            />
                            <RadioButton
                                label="Allow only trusted domains"
                                checked={redirection.mode === "allow_list"}
                                id="allow_list"
                                name="redirectMode"
                                onChange={() => {
                                    setFieldValue("redirection.mode", "allow_list");
                                    markAsChanged();
                                }}
                                helpText="Only domains in your allowed list will be accessible. All others will be blocked."
                            />
                        </BlockStack>
                    </BlockStack>
                </Card>

                {/* Section 3: Allowed Domains */}
                {redirection.mode === "allow_list" && (
                    <Card>
                        <BlockStack gap="400">
                            <Text variant="headingMd" as="h2">
                                Allowed Domains
                            </Text>
                            <Text tone="subdued" variant="bodySm">
                                Add trusted domains that users can access. Only domain names are stored (e.g., facebook.com, not https://facebook.com).
                            </Text>
                            <InlineStack gap="200" align="start" blockAlign="center">
                                <TextField
                                    label="Domain"
                                    value={domainInput}
                                    onChange={setDomainInput}
                                    placeholder="e.g., facebook.com"
                                    autoComplete="off"
                                    helpText="Enter domain name without http:// or https://"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddDomain();
                                        }
                                    }}
                                />
                                <Button onClick={handleAddDomain} disabled={!domainInput.trim()}>
                                    Add
                                </Button>
                            </InlineStack>
                            {allowedDomains.length > 0 && (
                                <BlockStack gap="200">
                                    <Text variant="bodyMd" fontWeight="semibold">
                                        Allowed Domains ({allowedDomains.length}):
                                    </Text>
                                    <Text tone="subdued" variant="bodySm">
                                        Click the X icon on any tag to remove a domain from the allowed list.
                                    </Text>
                                    <InlineStack gap="200" wrap>
                                        {allowedDomains.map((domain) => (
                                            <Tag key={domain} onRemove={() => handleRemoveDomain(domain)}>
                                                {domain}
                                            </Tag>
                                        ))}
                                    </InlineStack>
                                </BlockStack>
                            )}
                        </BlockStack>
                    </Card>
                )}

                {/* Section 5: Warning Message */}
                <Card>
                    <BlockStack gap="400">
                        <Text variant="headingMd" as="h2">
                            Warning Message
                        </Text>
                        <Checkbox
                            label="Show warning message"
                            checked={redirection.showWarningMessage !== false}
                            onChange={(val) => {
                                setFieldValue("redirection.showWarningMessage", val);
                                markAsChanged();
                            }}
                            helpText="Display a custom message when users attempt to access blocked external links."
                        />
                        {redirection.showWarningMessage && (
                            <TextField
                                label="Warning Message"
                                value={redirection.warningMessage || ""}
                                onChange={(val) => {
                                    setFieldValue("redirection.warningMessage", val);
                                    markAsChanged();
                                }}
                                multiline={4}
                                autoComplete="off"
                                helpText="This message will be displayed to users when they try to access a blocked external link."
                            />
                        )}
                    </BlockStack>
                </Card>

            </BlockStack>

            {toastMarkup}
            {hasUnsavedChanges && (
                <ContextSaveBar
                    message="You have unsaved changes"
                    saveAction={{
                        content: "Save",
                        onAction: handleSave,
                        loading: isCreateSettingLoading,
                        disabled: isCreateSettingLoading
                    }}
                    discardAction={{
                        content: "Discard",
                        onAction: handleDiscard,
                        disabled: isCreateSettingLoading
                    }}
                    visible={hasUnsavedChanges}
                />
            )}
            <br />
        </Page>
    );
};

export default RedirectPreventionPage;

